import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Share2,
  Copy,
  Check,
  Gift,
  Users,
  MessageCircle,
  Loader2,
  LogIn,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

export default function ShareReferralButton({ product, variant = "default" }) {
  const { user, token, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleOpen = async () => {
    if (!isAuthenticated) {
      setOpen(true);
      return;
    }
    setOpen(true);
    if (!referralCode) {
      setLoading(true);
      try {
        const response = await axios.post(
          `${API}/referrals/generate`,
          { product_id: product?.id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setReferralCode(response.data);
      } catch (error) {
        toast.error("Failed to generate referral link");
      } finally {
        setLoading(false);
      }
    }
  };

  const referralUrl = referralCode
    ? `${SITE_URL}${product ? `/mock-exams/${product.slug}` : "/mock-exams"}?ref=${referralCode.code}`
    : "";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const shareViaWhatsApp = () => {
    const text = product
      ? `Check out this mock exam: ${product.title}! Use my link for 10% off: ${referralUrl}`
      : `Get 10% off BECE mock exams at Great Messiah Publication! Use my link: ${referralUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const buttonClass = variant === "icon"
    ? ""
    : "rounded-full border-2 border-gray-200 text-gray-700 hover:border-accent hover:text-accent";

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          className="text-gray-500 hover:text-accent"
          data-testid="share-referral-icon-btn"
        >
          <Share2 className="w-5 h-5" />
        </Button>
      ) : (
        <Button
          variant="outline"
          onClick={handleOpen}
          className={buttonClass}
          data-testid="share-referral-btn"
        >
          <Gift className="w-4 h-4 mr-2" />
          Share & Earn
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="share-referral-modal">
          {/* Header */}
          <div className="bg-gradient-to-br from-accent to-red-500 text-white p-6">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5" />
              <span className="font-semibold">Refer & Earn</span>
            </div>
            <h3 className="text-2xl font-bold mb-1">Share. Earn. Repeat.</h3>
            <p className="text-sm text-white/80">
              Your friend gets <strong>10% off</strong>, you earn <strong>5% reward</strong> on every purchase!
            </p>
          </div>

          <div className="p-6">
            {!isAuthenticated ? (
              /* Not logged in */
              <div className="text-center py-4">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Sign in to get your unique referral link and start earning rewards.</p>
                <Button
                  asChild
                  className="rounded-full bg-navy-900 hover:bg-navy-800 text-white px-8"
                  data-testid="share-sign-in-btn"
                >
                  <a href="/login">
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In to Start Earning
                  </a>
                </Button>
              </div>
            ) : loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Generating your referral link...</p>
              </div>
            ) : referralCode ? (
              <div className="space-y-5">
                {/* Referral Link */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Your Referral Link</label>
                  <div className="flex gap-2">
                    <Input
                      value={referralUrl}
                      readOnly
                      className="rounded-xl text-sm bg-gray-50"
                      data-testid="referral-link-input"
                    />
                    <Button
                      onClick={copyToClipboard}
                      variant="outline"
                      className="shrink-0 rounded-xl"
                      data-testid="copy-referral-btn"
                    >
                      <AnimatePresence mode="wait">
                        {copied ? (
                          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                            <Check className="w-4 h-4 text-green-500" />
                          </motion.div>
                        ) : (
                          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                            <Copy className="w-4 h-4" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Button>
                  </div>
                </div>

                {/* Referral Code */}
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Your Referral Code</p>
                  <p className="text-2xl font-bold text-navy-900 tracking-wider" data-testid="referral-code-display">
                    {referralCode.code}
                  </p>
                </div>

                {/* Share buttons */}
                <div className="space-y-2">
                  <Button
                    onClick={shareViaWhatsApp}
                    className="w-full rounded-xl bg-green-600 hover:bg-green-700 text-white h-12"
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Share via WhatsApp
                  </Button>
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full rounded-xl h-12"
                  >
                    <Copy className="w-5 h-5 mr-2" />
                    Copy Link
                  </Button>
                </div>

                {/* How it works */}
                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">How it works</p>
                  <div className="space-y-3">
                    {[
                      { icon: Share2, text: "Share your unique link with friends" },
                      { icon: Sparkles, text: "They get 10% off their purchase" },
                      { icon: Gift, text: "You earn 5% reward on every sale" },
                    ].map((step, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                          <step.icon className="w-4 h-4 text-accent" />
                        </div>
                        <span>{step.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
